import { endOfDay, format, formatISO, startOfDay } from "date-fns";
import { isArray } from "lodash";
import {
  GridFilterItem,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import { DEFAULT_PAGE_SIZE, OperatorsMapper } from "./constans";

export const parsePaginationModel = (model?: Partial<GridPaginationModel>) => ({
  page: (model?.page ?? 0) + 1,
  size: model?.pageSize ?? DEFAULT_PAGE_SIZE,
});

export const parseSortModel = (model?: GridSortModel) => ({
  sort: model?.map(({ field, sort }) => `${field}:${sort}`),
});

const defaultOperator = "=like=";

type ParsedFilter = { field: string; value: string; operator: string };

/**
 * Resolves the RSQL operator for a MUI operator, falling back to `=like=`.
 */
const resolveOperator = (operator: string): string =>
  OperatorsMapper[operator as keyof typeof OperatorsMapper] || defaultOperator;

/**
 * Wraps a scalar / array value for RSQL: `isAnyOf` produces a parenthesised
 * list `(a,b,c)`, every other operator produces a quoted literal `"value"`.
 */
const formatValue = (
  value: GridFilterItem["value"],
  operator: string
): string => (operator === "isAnyOf" ? `(${value})` : `"${value}"`);

/**
 * Builds the filter(s) for a date-time column. A plain `is` is expanded into an
 * inclusive full-day range, while directional operators collapse to the start
 * or end of the day boundary.
 */
const buildDateTimeFilters = (
  { field, value, operator }: GridFilterItem,
  rsqlOperator: string
): ParsedFilter[] => {
  if (operator === "is") {
    return [
      { field, value: formatISO(startOfDay(value)), operator: "=ge=" },
      { field, value: formatISO(endOfDay(value)), operator: "=le=" },
    ];
  }

  if (["after", "onOrBefore"].includes(operator)) {
    return [
      { field, value: formatISO(endOfDay(value)), operator: rsqlOperator },
    ];
  }

  if (["onOrAfter", "before"].includes(operator)) {
    return [
      { field, value: formatISO(startOfDay(value)), operator: rsqlOperator },
    ];
  }

  return [];
};

/**
 * Converts a single grid filter item into zero or more RSQL filter parts.
 * The empty array means the item is skipped (empty value, empty array, etc.).
 */
const buildFiltersForItem = (
  filter: GridFilterItem,
  dateColumns?: string[],
  dateTimeColumns?: string[]
): ParsedFilter[] => {
  const { field, value, operator } = filter;
  const rsqlOperator = resolveOperator(operator);

  const hasValue = value != null && value !== "";
  const isDateTime = hasValue && !!dateTimeColumns?.includes(field);
  const isDate = hasValue && !!dateColumns?.includes(field);

  if (isDateTime) {
    return buildDateTimeFilters(filter, rsqlOperator);
  }

  if (isDate) {
    return [
      { field, value: format(value, "yyyy-MM-dd"), operator: rsqlOperator },
    ];
  }

  // Skip empty arrays and null/undefined scalars; everything else (a non-empty
  // array or any non-null scalar) is quoted the same way.
  const isEmpty = isArray(value) ? value.length === 0 : value == null;
  if (isEmpty) {
    return [];
  }

  return [
    { field, value: formatValue(value, operator), operator: rsqlOperator },
  ];
};

export const parseFilterModel = (
  items: GridFilterItem[] = [],
  dateColumns?: string[],
  dateTimeColumns?: string[]
) => ({
  filters: items
    .flatMap((filter) =>
      buildFiltersForItem(filter, dateColumns, dateTimeColumns)
    )
    .map(({ field, value, operator }) => `${field}${operator}${value}`)
    .join(";"),
});
